import React from 'react';
import { isResponderBadgeEnabled } from '../featureFlags';

/**
 * Responder badge (RESPONDER_BADGE_WANTLIST_CLOSET_PLAN.md, 2026-10-17).
 *
 * Reads `responderBadge` from a publicProfiles doc (or the enriched `owner`
 * object the cards build from one). The value is computed server-side in
 * functions/responderEvents.js from a ledger of every offer turn and interest
 * request the member owed an answer to; the client never sees the counts,
 * only the badge string: 'usually' | 'often_not' | 'rarely' | null.
 *
 * One presentation (Jason, 2026-10-17): a soft pill in the same style as the
 * membership badges, under the profile photo on the listing seller block and
 * the public profile, all three states. A thumbnail version was tried in
 * several forms (bare glyph, ActiveTrader-style tile, gold hexagon, gold
 * bubble) and dropped: a card is not the place for it, and the listing page
 * already carries the information.
 */

export const RESPONDER_BADGE_COPY = {
  usually: {
    label: 'Responsive Trader',
    meaning: 'Consistently answers offers and interest requests.',
  },
  often_not: {
    label: "Often doesn't respond",
    meaning: 'Repeatedly leaves requests unanswered, not just one missed offer.',
  },
  rarely: {
    label: 'Rarely responds',
    meaning: 'Most requests go unanswered.',
  },
};

// Gold for the positive badge (the site's emphasis colour, same family as
// the "Wants your pedal" ribbon); neutral grey for the negative ones. Blue is
// reserved for links and buttons.
const GOLD_TEXT = '#8A6519';

export function getResponderBadgeKey(profile) {
  const key = profile?.responderBadge;
  return key && RESPONDER_BADGE_COPY[key] ? key : null;
}

/**
 * The one responder glyph used everywhere (Jason, 2026-10-17: one icon,
 * consistent, outline not filled): a squared speech bubble with soft corners
 * and a centred tail, two text lines inside, drawn in a single stroke colour.
 * Gold on the positive badge, grey on the negative ones.
 */
function BubbleIcon({ size = 24, color = GOLD_TEXT }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'block', flexShrink: 0 }}
    >
      <path d="M5 4h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-4.5L12 20.5 9.5 17H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
      <path d="M8 9.5h8M8 12.5h5.5" />
    </svg>
  );
}

const ResponderBadge = ({ profile, style = {} }) => {
  if (!isResponderBadgeEnabled()) return null;
  const key = getResponderBadgeKey(profile);
  if (!key) return null;
  const copy = RESPONDER_BADGE_COPY[key];

  const positive = key === 'usually';
  return (
    <span
      className={`responder-badge responder-badge-${key}`}
      title={copy.meaning}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 12px 4px 10px',
        borderRadius: '9999px',
        fontFamily: 'Nunito, sans-serif',
        fontSize: '0.85rem',
        fontWeight: '700',
        letterSpacing: '0.01em',
        whiteSpace: 'nowrap',
        ...(positive
          ? { backgroundColor: '#FBF3DF', color: GOLD_TEXT }
          : { backgroundColor: '#E5E7EB', color: '#374151' }),
        ...style,
      }}
    >
      <BubbleIcon size={24} color={positive ? GOLD_TEXT : '#4B5563'} />
      {copy.label}
    </span>
  );
};

export default ResponderBadge;
